import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type DragEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuthStore } from '@/shared/auth/store';
import { WINDOW_ACCESS_REFUSAL, hasLoadedGrants, windowGateModeOf } from '@/shared/auth/screenAccess';
import type { AuthSession } from '@/shared/auth/types';
import {
  useTestersHubEnabled,
  useDemoSystemEnabled,
  useRfSimulatorEnabled,
  useTopScreensEnabled,
} from '@/shared/config/useFeatures';
import { useFavorites, type LegacyFavoriteResolver } from '@/shared/prefs/useFavorites';
import { NAV_EXPANDED_PREF, SIDEBAR_COLLAPSED_PREF } from '@/shared/prefs/storage';
import { usePrefState } from '@/shared/prefs/usePrefState';
import { ChevronLeftIcon, ChevronRightIcon, GripIcon, StarIcon } from '@/shared/components/icons';
import { GuardedNavLink } from '@/shared/ui/UnsavedChangesGuard';
import { dispatchAppShortcut } from '@/shared/ui/appShortcuts';
import {
  NAV_TREE,
  NAV_TREE_WITH_DEMO,
  withDemoNav,
  EXTRA_NAV_ITEMS,
  DEFAULT_EXPANDED,
  ID_TO_ACTION,
  resolveRoute,
  resolveLabel,
  navItemState,
  navPresenceDisagreements,
  isExtraVisible,
  isTestScreensAdmin,
  buildIdIndex,
  livePoints,
  findFirstIdForRoute,
  findAncestorIdsForRoute,
  type NavTreeItem,
  type NavTreeGroup,
  type ExtraNavItem,
  type RouteEntry,
} from './nav';
import { NavSearch } from './NavSearch';

/**
 * Left rail navigation, mirroring the PowerBuilder top-menu hierarchy.
 *
 * <ul>
 *   <li>Tree comes from {@link NAV_TREE} — projected at build time from
 *       {@code docs/pb-menu-tree.json} via {@code scripts/build-nav.mjs}.</li>
 *   <li>Live links: items whose PB window appears in {@link import('./nav').WINDOW_TO_ROUTE}
 *       render as {@link GuardedNavLink}s. Everything else greys out — there on purpose so
 *       the customer sees PB's full menu shape and can ask for the next port.</li>
 *   <li>Branches collapse/expand per user; state persists via {@code usePrefState}.</li>
 *   <li>Favorites pull from {@link useFavorites}; only items with live routes can be starred
 *       (you can't favorite a placeholder). slice-3641: a favourite stores the NAV ITEM ID,
 *       not the route — six PB leaf pairs share one route (starring "Tag Order Search" showed
 *       "Direct/Bulk Tag Order Search") and the demo-only leaves aren't in the static tree at
 *       all (starring RF Simulator lit the star but listed nothing). Rows resolve against the
 *       demo-aware tree this component renders, so a demo leaf's star is live on the demo box
 *       and simply invisible elsewhere. PH10 extras have no PB id — they keep their route as
 *       their key (unique by construction).</li>
 *   <li>slice-3651: Favorites rows are drag-to-reorder (grip handle, or ArrowUp/ArrowDown on
 *       the handle). The pref array IS the display order, so a drop simply rewrites it.</li>
 * </ul>
 */
export function Sidebar() {
  const session = useAuthStore((s) => s.session);
  const location = useLocation();
  // slice-3608 (corrects slice-3590): a NEW login writes this false (sidebar open) and resets the
  // tree pref below to {} (every branch closed) — useLogin.onSuccess; toggles persist per session.
  const [collapsed, setCollapsed] = usePrefState<boolean>(SIDEBAR_COLLAPSED_PREF, false);
  // Slice-319: pref key bumped to v2. v1 was seeded from DEFAULT_EXPANDED which eagerly
  // opened every group containing a live route -- with ~400 LIVE screens that meant most of
  // the sidebar was open on first load and stayed that way until the user collapsed
  // everything by hand. v2 starts empty and the active-route effect below auto-expands just
  // the trail to wherever the operator is. Existing users get the new collapsed default
  // because the v2 key doesn't exist in their localStorage yet.
  const [expanded, setExpanded] = usePrefState<Record<string, boolean>>(
    NAV_EXPANDED_PREF,
    DEFAULT_EXPANDED,
  );
  // Auto-expand the ancestor trail to the active route so a deep-link (or a manual URL
  // paste) reveals exactly where the screen lives in PB's menu hierarchy. Additive only --
  // we never collapse a branch the operator opened, just merge in the trail for the
  // current pathname. Re-runs whenever the URL pathname changes (route navigation).
  useEffect(() => {
    const trail = findAncestorIdsForRoute(location.pathname);
    if (!trail || trail.length === 0) return;
    setExpanded((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of trail) {
        if (!next[id]) {
          next[id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  // When the user is actively typing a search, hide Favorites / tree / Extras so the dropdown
  // is the only thing on screen. The dropdown renders inline (not absolute) — needed because
  // the sidebar uses overflow-y-auto, which clips floating popups.
  const [searching, setSearching] = useState(false);

  const isExpanded = useCallback((id: string) => !!expanded[id], [expanded]);
  const toggleExpand = useCallback(
    (id: string) =>
      setExpanded((prev) => {
        const next = { ...prev };
        if (next[id]) delete next[id];
        else next[id] = true;
        return next;
      }),
    [setExpanded],
  );

  // Pre-compute favorite display rows — a fav holds a NAV ITEM ID (slice-3641), so we resolve
  // back to THAT leaf's PB caption + route (and ACL re-filter against the user's current
  // modules in case access was revoked since starring).
  const moduleLevels = useMemo(() => session?.moduleLevels ?? {}, [session]);
  // slice-3609: the customer DEMO instance runs with the testers hub switched off.
  const testersHubEnabled = useTestersHubEnabled();
  // slice-3930: the TEMPORARY "My top screens" survey link (backend flag, ph10-test only).
  const topScreensEnabled = useTopScreensEnabled();
  // slice-3626 (ADR-0050): demo-instance-only leaves (Reports > Labor, Activities > RF Simulator).
  const demoSystemEnabled = useDemoSystemEnabled();
  // slice-4482: Activities > RF Simulator also on a non-demo box that switches it on (ph10-test).
  const rfSimulatorEnabled = useRfSimulatorEnabled();
  const navTree = useMemo(
    () => withDemoNav(NAV_TREE, demoSystemEnabled, rfSimulatorEnabled),
    [demoSystemEnabled, rfSimulatorEnabled],
  );
  const isAdmin = isTestScreensAdmin(session?.userId);

  // slice-3641: one-time upgrade of favourites written before ids. A stored route path maps to
  // the FIRST menu leaf that owns it (NAV_TREE_WITH_DEMO — the full tree, so a demo screen's
  // star isn't thrown away on an instance whose flag is off); a PH10 extra keeps its route as
  // its key; anything else is dropped.
  const resolveLegacyFavorite = useCallback<LegacyFavoriteResolver>((value) => {
    if (EXTRA_NAV_ITEMS.some((e) => e.to === value)) return value;
    return findFirstIdForRoute(value, NAV_TREE_WITH_DEMO);
  }, []);
  const { favorites, isFavorite, toggle, move } = useFavorites(resolveLegacyFavorite);

  const idIndex = useMemo(() => buildIdIndex(navTree), [navTree]);
  const favRows = useMemo(() => {
    return favorites
      .map((favId) => {
        const point = idIndex.get(favId);
        if (point) {
          // ADR-0067: only a screen the user can open is listed (a greyed favourite is noise).
          if (navItemState(point.item, moduleLevels, session) !== 'enabled') return null;
          return { key: favId, item: point.item, to: navTargetFor(point.item, point.route) };
        }
        // PH10 extras (Printer Configuration, Testers Hub, …) are keyed by their route — they
        // have no PB menu id. Before slice-3641 they were starrable but never listed here.
        const extra = EXTRA_NAV_ITEMS.find((e) => e.to === favId);
        if (extra) {
          if (!isExtraVisible(extra, moduleLevels)) return null;
          if (extra.adminOnly && !isAdmin) return null;
          if (extra.hubOnly && !testersHubEnabled) return null;
          if (extra.topScreensOnly && !topScreensEnabled) return null;
          return { key: favId, item: { id: extra.to, label: extra.label }, to: extra.to };
        }
        // Unknown id: a demo leaf on a non-demo instance, or a screen that left the menu.
        // Deliberately NOT removed from the pref — it comes back when the leaf does.
        return null;
      })
      .filter((r): r is NonNullable<typeof r> => r != null);
  }, [favorites, idIndex, moduleLevels, session, isAdmin, testersHubEnabled, topScreensEnabled]);

  // ADR-0067 log-only soak: one console.debug per session summarising where window presence and
  // today's module-level rule disagree. Never sent to the server (a line per item would flood).
  const sessionId = session?.sessionId;
  useEffect(() => {
    if (!session || !hasLoadedGrants(session) || !sessionId || summarisedSessions.has(sessionId)) return;
    summarisedSessions.add(sessionId);
    const d = navPresenceDisagreements(navTree, moduleLevels, session);
    if (d.levelOnly + d.presenceOnly === 0) return;
    console.debug(
      `WINDOW-GATE sidebar (${windowGateModeOf(session)}): ${d.levelOnly} item(s) shown by module level ` +
        `but not granted by window; ${d.presenceOnly} granted by window but hidden by module level`,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, session]);

  if (!session) return null;

  if (collapsed) {
    return (
      <nav className="flex w-9 shrink-0 flex-col items-center border-r border-phblue-900 bg-phblue-800 py-2">
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="rounded p-1 text-phblue-200 hover:bg-phblue-600/50 hover:text-white"
          title="Expand sidebar"
          aria-label="Expand sidebar"
        >
          <ChevronRightIcon />
        </button>
      </nav>
    );
  }

  return (
    <nav className="flex w-64 shrink-0 flex-col gap-1 overflow-y-auto border-r border-phblue-900 bg-phblue-800 p-2 text-sm text-white">
      <div className="flex items-center justify-between px-2 py-1">
        {/* Slice-472 (FB#232): clicking "Navigation" collapses all expanded
            branches. setExpanded({}) wins over the per-branch trail seeded
            by useEffect on route change because the user's explicit action
            takes precedence -- they can re-click to walk the tree again. */}
        <button
          type="button"
          onClick={() => setExpanded({})}
          className="rounded px-1 py-0.5 text-[10px] uppercase tracking-wide text-phblue-300 hover:bg-phblue-600/50 hover:text-white focus:outline-none focus:ring-1 focus:ring-phblue-300"
          title="Collapse all expanded branches"
          aria-label="Collapse all expanded navigation branches"
        >
          Navigation
        </button>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="rounded p-0.5 text-phblue-200 hover:bg-phblue-600/50 hover:text-white"
          title="Collapse sidebar"
          aria-label="Collapse sidebar"
        >
          <ChevronLeftIcon />
        </button>
      </div>

      <NavSearch moduleLevels={moduleLevels} onSearchingChange={setSearching} />

      {!searching && favRows.length > 0 && (
        <FavoritesList rows={favRows} toggle={toggle} move={move} />
      )}

      {!searching &&
        navTree.map((group) => (
          <GroupSection
            key={group.id}
            group={group}
            isExpanded={isExpanded}
            toggleExpand={toggleExpand}
            isFavorite={isFavorite}
            toggleStar={toggle}
            moduleLevels={moduleLevels}
            session={session}
          />
        ))}

      {!searching && (
        <ExtrasGroup
          items={EXTRA_NAV_ITEMS}
          moduleLevels={moduleLevels}
          isFavorite={isFavorite}
          toggleStar={toggle}
          isAdmin={isAdmin}
          hubEnabled={testersHubEnabled}
          topScreensEnabled={topScreensEnabled}
        />
      )}
    </nav>
  );
}

// campaign wave-4 (#879/#880): some PB menu items open the SAME window in a different launch
// mode (gstr_winparm booleans). WINDOW_TO_ROUTE is window-keyed, so mode-bearing menu items
// get a per-menu-id target override here.
const MENU_ID_ROUTE_OVERRIDES: Record<string, string> = {
  // PB m_newtruck opens w_truck_search with boolean1='Y' (free-form + insertrow).
  m_newtruck: '/browse/trucks?new=1',
};

/**
 * The URL a menu leaf navigates to. Hoisted out of {@code NavNode} in slice-3641 so a
 * FAVOURITE row lands on exactly what the tree row would — before, a starred "New Truck" lost
 * its {@code ?new=1} mode and a starred placeholder lost its {@code ?id=} disambiguator.
 *
 * <p>Slice-286: placeholder routes get the menu-item id appended as {@code ?id=…} so
 * PbPlaceholderPage can show the *clicked* item's label + path. Without it, 271 distinct items
 * under m_reports all share window=w_pre_report and the placeholder would render "Lot Recall -
 * BOM" regardless of which one was clicked.
 */
function navTargetFor(item: NavTreeItem, route: RouteEntry): string {
  const override = item.id ? MENU_ID_ROUTE_OVERRIDES[item.id] : undefined;
  if (override) return override;
  if (route.placeholder && item.id) return `${route.to}?id=${encodeURIComponent(item.id)}`;
  return route.to;
}

/**
 * slice-3658: for every bare route the sidebar links to, the query-bearing targets that share
 * its pathname (New Truck = /browse/trucks?new=1 beside Truck Search = /browse/trucks; the
 * placeholder ?id= leaves). The bare link passes these to GuardedNavLink so it stands down
 * when one of them is the current URL, instead of both rows highlighting.
 */
const QUERY_TARGETS_BY_PATH: ReadonlyMap<string, readonly string[]> = (() => {
  const m = new Map<string, string[]>();
  for (const { item, route } of livePoints(NAV_TREE_WITH_DEMO)) {
    const target = navTargetFor(item, route);
    const qi = target.indexOf('?');
    if (qi < 0) continue;
    const path = target.slice(0, qi);
    const list = m.get(path) ?? [];
    if (!list.includes(target)) list.push(target);
    m.set(path, list);
  }
  return m;
})();

function yieldsFor(to: string): readonly string[] | undefined {
  if (to.includes('?')) return undefined;
  return QUERY_TARGETS_BY_PATH.get(to);
}

/** Sessions whose log-mode sidebar disagreement summary was already printed (once each). */
const summarisedSessions = new Set<string>();

type FavoriteDisplayRow = { key: string; item: NavTreeItem; to: string };
type DropPlace = 'before' | 'after';

/** Top half of the hovered row drops BEFORE it, bottom half AFTER — the usual list convention. */
function dropPlaceFor(e: DragEvent<HTMLElement>): DropPlace {
  const r = e.currentTarget.getBoundingClientRect();
  return e.clientY < r.top + r.height / 2 ? 'before' : 'after';
}

/**
 * slice-3651 (Chris 2026-09-15: "for favorites - can we make it so that users can drag and
 * choose the position/order of them"). Native HTML5 drag-and-drop, no library: every row is
 * draggable, the hovered row shows an amber line on the half you will land in, and the drop
 * calls {@link useFavorites#move} which rewrites the pref array — the array IS the order, so
 * nothing else needs to change. The grip handle is also a keyboard target: ArrowUp/ArrowDown
 * swap the row with its visible neighbour, for operators who don't use a mouse.
 *
 * <p>Order is kept relative to the VISIBLE neighbour (a hidden favourite — a demo leaf on a
 * non-demo box, a revoked module — stays in the pref where it was, per slice-3641).
 *
 * <p>Row keys are nav-item ids: two leaves sharing a route (Tag Order Search / Direct-Bulk
 * Tag Order Search) must render as two distinct rows.
 */
function FavoritesList({
  rows,
  toggle,
  move,
}: {
  rows: FavoriteDisplayRow[];
  toggle: (id: string) => void;
  move: (id: string, targetId: string, place: DropPlace) => void;
}) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; place: DropPlace } | null>(null);
  const endDrag = useCallback(() => {
    setDragId(null);
    setOver(null);
  }, []);

  return (
    <Section label="Favorites">
      {rows.map(({ key, item, to }, i) => {
        const label = resolveLabel(item) ?? '';
        const indicator = over && over.id === key && dragId && dragId !== key ? over.place : null;
        const onGripKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
          if (e.key === 'ArrowUp' && i > 0) {
            e.preventDefault();
            move(key, rows[i - 1]!.key, 'before');
          } else if (e.key === 'ArrowDown' && i < rows.length - 1) {
            e.preventDefault();
            move(key, rows[i + 1]!.key, 'after');
          }
        };
        return (
          <div
            key={`fav:${key}`}
            data-testid={`favorite-row:${key}`}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
              // Firefox refuses to start a drag with no data; the id is also the fallback
              // source on drop should React state lag the native event.
              e.dataTransfer.setData('text/plain', key);
              setDragId(key);
            }}
            onDragOver={(e) => {
              if (!dragId || dragId === key) return;
              e.preventDefault(); // allow the drop
              e.dataTransfer.dropEffect = 'move';
              const place = dropPlaceFor(e); // read the target BEFORE the updater (slice-3617)
              setOver((prev) =>
                prev && prev.id === key && prev.place === place ? prev : { id: key, place },
              );
            }}
            onDragLeave={(e) => {
              // Leaving for a child element fires dragleave too — keep the indicator then.
              if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
              setOver((prev) => (prev?.id === key ? null : prev));
            }}
            onDrop={(e) => {
              e.preventDefault();
              const from = dragId ?? e.dataTransfer.getData('text/plain');
              if (from && from !== key) move(from, key, dropPlaceFor(e));
              endDrag();
            }}
            onDragEnd={endDrag}
            className={clsx('group relative rounded', dragId === key && 'opacity-50')}
          >
            {indicator && (
              <div
                aria-hidden
                data-testid="favorite-drop-indicator"
                className={clsx(
                  'pointer-events-none absolute inset-x-1 z-10 h-0.5 rounded bg-amber-400',
                  indicator === 'before' ? '-top-px' : '-bottom-px',
                )}
              />
            )}
            <LeafRow item={item} to={to} starred onToggleStar={() => toggle(key)} depth={0} />
            <button
              type="button"
              onKeyDown={onGripKeyDown}
              title="Drag to reorder (or press ↑ / ↓)"
              aria-label={`Reorder ${label}`}
              className="absolute left-0.5 top-1/2 flex h-5 w-4 -translate-y-1/2 cursor-grab items-center justify-center rounded text-phblue-400 opacity-0 transition-opacity hover:text-white focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-phblue-300 group-hover:opacity-100 active:cursor-grabbing"
            >
              <GripIcon size={12} />
            </button>
          </div>
        );
      })}
    </Section>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="px-2 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-phblue-200">
        {label}
      </div>
      {children}
    </div>
  );
}

function GroupSection({
  group,
  isExpanded,
  toggleExpand,
  isFavorite,
  toggleStar,
  moduleLevels,
  session,
}: {
  group: NavTreeGroup;
  isExpanded: (id: string) => boolean;
  toggleExpand: (id: string) => void;
  /** slice-3641: keyed by nav-item id (PH10 extras, which have no PB id, use their route). */
  isFavorite: (id: string) => boolean;
  toggleStar: (id: string) => void;
  moduleLevels: Record<string, number>;
  session: AuthSession;
}) {
  const open = isExpanded(group.id);
  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        onClick={() => toggleExpand(group.id)}
        className="flex items-center gap-1 px-2 pt-2 pb-1 text-left text-xs font-semibold uppercase tracking-wide text-phblue-200 hover:text-white"
        aria-expanded={open}
      >
        <Caret open={open} />
        {group.label}
      </button>
      {open && (
        <div className="flex flex-col gap-0.5">
          {group.children.map((child, i) => (
            <NavNode
              key={child.id || `sep:${i}`}
              item={child}
              depth={1}
              isExpanded={isExpanded}
              toggleExpand={toggleExpand}
              isFavorite={isFavorite}
              toggleStar={toggleStar}
              moduleLevels={moduleLevels}
              session={session}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NavNode({
  item,
  depth,
  isExpanded,
  toggleExpand,
  isFavorite,
  toggleStar,
  moduleLevels,
  session,
}: {
  item: NavTreeItem;
  depth: number;
  isExpanded: (id: string) => boolean;
  toggleExpand: (id: string) => void;
  /** slice-3641: keyed by nav-item id (PH10 extras, which have no PB id, use their route). */
  isFavorite: (id: string) => boolean;
  toggleStar: (id: string) => void;
  moduleLevels: Record<string, number>;
  session: AuthSession;
}) {
  if (item.isSeparator) {
    return <hr className="my-0.5 border-phblue-700" />;
  }
  const hasChildren = !!item.children?.length;
  const route = resolveRoute(item);
  const state = navItemState(item, moduleLevels, session);
  if (state === 'hidden') return null;

  if (hasChildren) {
    const open = isExpanded(item.id);
    return (
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          onClick={() => toggleExpand(item.id)}
          className="flex items-center gap-1 rounded px-2 py-0.5 text-left text-xs font-medium text-phblue-100 hover:bg-phblue-600/50"
          style={{ paddingLeft: 8 + depth * 12 }}
          aria-expanded={open}
        >
          <Caret open={open} />
          {resolveLabel(item)}
        </button>
        {open && (
          <div className="flex flex-col gap-0.5">
            {item.children!.map((c, i) => (
              <NavNode
                key={c.id || `sep:${i}`}
                item={c}
                depth={depth + 1}
                isExpanded={isExpanded}
                toggleExpand={toggleExpand}
                isFavorite={isFavorite}
                toggleStar={toggleStar}
                moduleLevels={moduleLevels}
                session={session}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (route && state === 'disabled') {
    // ADR-0067 enforce mode, HideDisabledMenu = 'N': PB greys the item and it cannot be
    // clicked (f_phfc_menu_security.srf:62-70). Unreachable while the gate is log-only.
    return (
      <div
        role="link"
        aria-disabled="true"
        className="cursor-not-allowed rounded py-0.5 pr-7 text-xs text-phblue-400 opacity-60"
        style={{ paddingLeft: 8 + depth * 12 + 14 }}
        title={WINDOW_ACCESS_REFUSAL}
      >
        {resolveLabel(item)}
      </div>
    );
  }

  if (route) {
    // slice-3641: the star is keyed by the MENU ID, so the two leaves that share a route
    // (e.g. m_tagordersearch / m_directbulktagordersearch) star independently.
    const favKey = item.id || route.to;
    return (
      <LeafRow
        item={item}
        to={navTargetFor(item, route)}
        starred={isFavorite(favKey)}
        onToggleStar={() => toggleStar(favKey)}
        depth={depth}
        placeholder={!!route.placeholder}
      />
    );
  }

  // slice-3454: PB leaves whose clicked event pokes the running app instead of opening a
  // window (Help > Help Contents F1, Help > Menu Navigator F9). No route to link to, so the
  // row is a button that fires the same app-shortcut event the accelerator does.
  const action = item.id ? ID_TO_ACTION[item.id] : undefined;
  if (action) {
    return (
      <button
        type="button"
        onClick={() => dispatchAppShortcut(action.event)}
        className="rounded py-0.5 pr-7 text-left text-xs text-phblue-100 hover:bg-phblue-600/50"
        style={{ paddingLeft: 8 + depth * 12 + 14 }}
        title={action.hint}
      >
        {resolveLabel(item)}
        <span className="ml-1 text-[10px] text-phblue-400">{action.shortcut}</span>
      </button>
    );
  }

  // No route at all (extremely rare now that placeholder routes are auto-generated for the
  // rest of the PB menu) — render dimmed + non-clickable as a last resort.
  return (
    <div
      className="rounded px-2 py-0.5 text-xs text-phblue-300"
      style={{ paddingLeft: 8 + depth * 12 + 14 /* match caret indent on branches */ }}
      title="Not available yet"
    >
      {resolveLabel(item)}
    </div>
  );
}

function LeafRow({
  item,
  to,
  starred,
  onToggleStar,
  depth,
  placeholder = false,
}: {
  item: NavTreeItem;
  to: string;
  starred: boolean;
  onToggleStar: () => void;
  depth: number;
  /** Placeholder routes go to /pb/:window — same chrome as a real screen so the user can
   *  find their bearings, just visibly dimmer + italic in the sidebar to signal "not yet
   *  ported, click to see what we know about it". */
  placeholder?: boolean;
}) {
  return (
    <div className="group relative flex items-center">
      <GuardedNavLink
        to={to}
        yieldToQueries={yieldsFor(to)}
        className={({ isActive }) =>
          clsx(
            'flex-1 rounded py-0.5 pr-7 text-xs transition-colors',
            isActive
              ? 'bg-phblue-700 text-white'
              : placeholder
                ? 'italic text-phblue-300 hover:bg-phblue-600/30 hover:text-phblue-100'
                : 'text-phblue-100 hover:bg-phblue-600/50',
          )
        }
        style={{ paddingLeft: 8 + depth * 12 + 14 }}
        title={placeholder ? 'PB screen — not yet ported' : undefined}
      >
        {resolveLabel(item)}
      </GuardedNavLink>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleStar();
        }}
        title={starred ? 'Remove from favorites' : 'Add to favorites'}
        aria-label={
          starred
            ? `Remove ${resolveLabel(item) ?? ''} from favorites`
            : `Add ${resolveLabel(item) ?? ''} to favorites`
        }
        aria-pressed={starred}
        className={clsx(
          'absolute right-1 flex h-5 w-5 items-center justify-center rounded transition-colors hover:text-amber-500',
          starred ? 'text-amber-400' : 'text-phblue-400',
        )}
      >
        <StarIcon size={14} filled={starred} />
      </button>
    </div>
  );
}

function ExtrasGroup({
  items,
  moduleLevels,
  isFavorite,
  toggleStar,
  isAdmin,
  hubEnabled,
  topScreensEnabled,
}: {
  items: ExtraNavItem[];
  moduleLevels: Record<string, number>;
  /** slice-3641: keyed by nav-item id (PH10 extras, which have no PB id, use their route). */
  isFavorite: (id: string) => boolean;
  toggleStar: (id: string) => void;
  isAdmin: boolean;
  hubEnabled: boolean;
  topScreensEnabled: boolean;
}) {
  const visible = items.filter(
    (it) =>
      isExtraVisible(it, moduleLevels) &&
      (!it.adminOnly || isAdmin) &&
      (!it.hubOnly || hubEnabled) &&
      (!it.topScreensOnly || topScreensEnabled),
  );
  if (visible.length === 0) return null;
  return (
    <div className="mt-2 flex flex-col gap-0.5 border-t border-phblue-700 pt-2">
      <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-phblue-300">
        PH10
      </div>
      {visible.map((it) => (
        <LeafRow
          key={it.to}
          item={{ id: it.to, label: it.label }}
          to={it.to}
          starred={isFavorite(it.to)}
          onToggleStar={() => toggleStar(it.to)}
          depth={0}
        />
      ))}
    </div>
  );
}

function Caret({ open }: { open: boolean }) {
  return (
    <span
      aria-hidden
      className={clsx(
        'inline-block w-3 text-phblue-300 transition-transform',
        open && 'rotate-90',
      )}
    >
      ▸
    </span>
  );
}
